/** The legacy role column as the tier mapping reads it — spelled here so this package depends on nothing of the application's; `organisationRoles.test.ts` pins it to `UserRole`. */
export type LegacyUserRole = 'editor' | 'user' | 'supplier'

/** The user fields the organisation-role mapping reads. */
export type UserRoleTierConfig = {
  role: LegacyUserRole
  hasConfigPermissions: boolean
  isViewOnly: boolean
  /** users.custom_role_id — the one custom role the user holds directly, or null. */
  customRoleId: string | null
  /** The custom roles of the user's live groups (groups.custom_role_id), deduplicated. */
  groupCustomRoleIds: readonly string[]
}

/** Whether any custom role, direct or through a group, replaces the user's legacy tier. */
export const holdsCustomRole = (user: Pick<UserRoleTierConfig, 'customRoleId' | 'groupCustomRoleIds'>): boolean =>
  user.customRoleId != null || user.groupCustomRoleIds.length > 0

/**
 * The organisation relations the sync pipeline derives from users.role — one per legacy role
 * tier. The `derived_` prefix marks them recomputable/sync-owned (never granted via the API).
 * `derived_owner` (super admins) carries full configuration access; the other tiers are the
 * legacy floors every org-wide permission unions.
 */
export const DERIVED_ORGANISATION_ROLES = ['derived_owner', 'derived_admin', 'derived_viewer', 'derived_user'] as const

/** An organisation role relation derived from users.role. */
export type DerivedOrganisationRole = (typeof DERIVED_ORGANISATION_ROLES)[number]

/**
 * Maps a user's role tier to their derived organisation relation. These are our durable authz
 * identifiers, deliberately decoupled from the product's user-facing role labels so a label
 * rename can't silently rewrite persisted tuples. Suppliers hold no organisation role.
 *
 * A custom role — held directly or through a group — replaces the legacy tier: the tiers are
 * additive floors the model cannot subtract from, so a user whose access is meant to come from a
 * role's grants must hold no tier at all. Super admins are the exception — configuration access is
 * a separate axis from entity permissions, so `derived_owner` survives a role assignment.
 */
export const mapUserToOrganisationRole = (user: UserRoleTierConfig): DerivedOrganisationRole | null => {
  const tierReplaced = holdsCustomRole(user)
  switch (user.role) {
    case 'supplier':
      return null
    case 'user':
      return tierReplaced ? null : 'derived_user'
    case 'editor':
      if (user.isViewOnly) return tierReplaced ? null : 'derived_viewer'
      if (user.hasConfigPermissions) return 'derived_owner'
      return tierReplaced ? null : 'derived_admin'
  }
}
